import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { Badge, Box, Button, Callout, Card, Code, Flex, Text } from "@radix-ui/themes";
import toast from "react-hot-toast";
import { AlertCircle, Bell, CheckCircle2, Clock, Info, KeyRound, Send, XCircle } from "lucide-react";
import InputField from "@/components/form/InputField";
import SelectField from "@/components/form/SelectField";
import { anythinkPayService } from "@/services/breeze/AnythinkPayService";
import { UpdateAppleIapCredentialsRequest } from "@/types/breeze/AnythinkPay";
import { LoadingScreen } from "@/components/ui/LoadingScreen";

const validationSchema = Yup.object({
  bundle_id: Yup.string().required("Bundle ID is required"),
  environment: Yup.string().oneOf(["sandbox", "production"]).required(),
  asc_issuer_id: Yup.string(),
  asc_key_id: Yup.string(),
  asc_private_key_pem: Yup.string(),
});

interface FormValues {
  bundle_id: string;
  environment: string;
  asc_issuer_id: string;
  asc_key_id: string;
  asc_private_key_pem: string;
}

function SaveButton({ pending }: { pending: boolean }) {
  const { dirty } = useFormikContext<FormValues>();
  return (
    <Button type="submit" loading={pending} disabled={!dirty || pending}>
      Save Apple settings
    </Button>
  );
}

type TestState =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "polling"; token: string; pollsRemaining: number }
  | { kind: "success"; result: string; attemptedAt?: string; receivedLocally: boolean; receivedAt?: string }
  | { kind: "failure"; result: string; attemptedAt?: string }
  | { kind: "timeout"; receivedLocally: boolean; receivedAt?: string }
  | { kind: "error"; message: string };

type AppleEnv = "sandbox" | "production";

function TestEnvironmentPanel({
  env,
  state,
  onRun,
}: {
  env: AppleEnv;
  state: TestState;
  onRun: () => void;
}) {
  const inFlight = state.kind === "sending" || state.kind === "polling";
  const label = env === "sandbox" ? "Sandbox" : "Production";
  const buttonText =
    state.kind === "sending"
      ? "Asking Apple…"
      : state.kind === "polling"
        ? `Waiting (${state.pollsRemaining * 3}s)…`
        : `Test ${label.toLowerCase()}`;
  const color = env === "sandbox" ? "blue" : "amber";
  return (
    <Box style={{ flex: "1 1 280px", minWidth: 260 }}>
      <Flex align="center" gap="2" mb="2">
        <Badge color={color}>{label}</Badge>
      </Flex>
      <Button type="button" variant="outline" onClick={onRun} disabled={inFlight}>
        <Send size={14} />
        {buttonText}
      </Button>
      {state.kind === "success" && (
        <Box mt="2">
          {state.receivedLocally ? (
            <Callout.Root size="1" color="green">
              <Callout.Icon><CheckCircle2 size={14} /></Callout.Icon>
              <Callout.Text>
                <b>End-to-end confirmed.</b> Apple delivered ({state.result}) and Anythink's
                handler processed the payload.
                {state.attemptedAt && (
                  <> Apple attempted at {new Date(state.attemptedAt).toLocaleString()}.</>
                )}
                {state.receivedAt && (
                  <> Received at {new Date(state.receivedAt).toLocaleString()}.</>
                )}
              </Callout.Text>
            </Callout.Root>
          ) : (
            <Callout.Root size="1" color="amber">
              <Callout.Icon><AlertCircle size={14} /></Callout.Icon>
              <Callout.Text>
                Apple delivered to the {label.toLowerCase()} URL ({state.result}), but
                <b> this Anythink instance never saw the payload</b>. The URL in App Store
                Connect for {label.toLowerCase()} is probably pointing at a different host
                (e.g. your existing prod deployment, not the one running this build).
              </Callout.Text>
            </Callout.Root>
          )}
        </Box>
      )}
      {state.kind === "failure" && (
        <Callout.Root size="1" color="red" mt="2">
          <Callout.Icon><XCircle size={14} /></Callout.Icon>
          <Callout.Text>
            Apple tried but failed: <b>{state.result}</b>. Check the {label.toLowerCase()} URL
            is reachable from the public internet, returns 2xx, and that the bundle ID matches
            App Store Connect exactly.
          </Callout.Text>
        </Callout.Root>
      )}
      {state.kind === "timeout" && (
        <Box mt="2">
          {state.receivedLocally ? (
            <Callout.Root size="1" color="green">
              <Callout.Icon><CheckCircle2 size={14} /></Callout.Icon>
              <Callout.Text>
                Anythink processed the test payload
                {state.receivedAt && (
                  <> at {new Date(state.receivedAt).toLocaleString()}</>
                )}
                {" "}— but Apple hasn't reported a delivery status yet. Their API is sometimes
                slower than the webhook itself; you can treat this as success.
              </Callout.Text>
            </Callout.Root>
          ) : (
            <Callout.Root size="1" color="orange">
              <Callout.Icon><Clock size={14} /></Callout.Icon>
              <Callout.Text>
                No result after 60s. Apple may still be retrying — check back, or watch
                incoming requests on your {label.toLowerCase()} URL.
              </Callout.Text>
            </Callout.Root>
          )}
        </Box>
      )}
      {state.kind === "error" && (
        <Callout.Root size="1" color="red" mt="2">
          <Callout.Icon><XCircle size={14} /></Callout.Icon>
          <Callout.Text>{state.message}</Callout.Text>
        </Callout.Root>
      )}
    </Box>
  );
}

export default function AppleIapSetup() {
  const queryClient = useQueryClient();
  const [testStates, setTestStates] = useState<Record<AppleEnv, TestState>>({
    sandbox: { kind: "idle" },
    production: { kind: "idle" },
  });

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  const setTestState = (env: AppleEnv, state: TestState) => {
    if (!mountedRef.current) return;
    setTestStates((prev) => ({ ...prev, [env]: state }));
  };

  const { data, isLoading } = useQuery({
    queryKey: ["apple-iap-credentials"],
    queryFn: () => anythinkPayService.getAppleIapCredentials(),
  });

  const save = useMutation({
    mutationFn: (req: UpdateAppleIapCredentialsRequest) =>
      anythinkPayService.updateAppleIapCredentials(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apple-iap-credentials"] });
    },
  });

  // Poll every 3s up to ~60s; sandbox and production are independent and tested separately.
  const runTest = async (env: AppleEnv) => {
    setTestState(env, { kind: "sending" });
    let token: string;
    try {
      const r = await anythinkPayService.requestAppleTestNotification(env);
      token = r.test_notification_token;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to request test notification";
      setTestState(env, { kind: "error", message: msg });
      toast.error(msg);
      return;
    }

    const maxPolls = 20;
    setTestState(env, { kind: "polling", token, pollsRemaining: maxPolls });
    let lastReceivedLocally = false;
    let lastReceivedAt: string | undefined;
    for (let i = 0; i < maxPolls; i++) {
      await new Promise((res) => setTimeout(res, 3000));
      try {
        const status = await anythinkPayService.getAppleTestNotificationStatus(token, env);
        lastReceivedLocally = status.received_locally;
        lastReceivedAt = status.received_at ?? undefined;
        if (status.result && status.result !== "PENDING") {
          if (status.result === "SUCCESS") {
            setTestState(env, {
              kind: "success",
              result: status.result,
              attemptedAt: status.attempted_at ?? undefined,
              receivedLocally: status.received_locally,
              receivedAt: status.received_at ?? undefined,
            });
          } else {
            setTestState(env, {
              kind: "failure",
              result: status.result,
              attemptedAt: status.attempted_at ?? undefined,
            });
          }
          return;
        }
        setTestState(env, { kind: "polling", token, pollsRemaining: maxPolls - i - 1 });
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to fetch test notification status";
        setTestState(env, { kind: "error", message: msg });
        return;
      }
    }
    setTestState(env, {
      kind: "timeout",
      receivedLocally: lastReceivedLocally,
      receivedAt: lastReceivedAt,
    });
  };

  if (isLoading) return <LoadingScreen fullPage={false} />;

  const initialValues: FormValues = {
    bundle_id: data?.bundle_id ?? "",
    environment: data?.environment ?? "sandbox",
    asc_issuer_id: data?.asc_issuer_id ?? "",
    asc_key_id: data?.asc_key_id ?? "",
    asc_private_key_pem: "",
  };

  const handleSubmit = (values: FormValues) => {
    const req: UpdateAppleIapCredentialsRequest = {
      bundle_id: values.bundle_id,
      environment: values.environment,
      asc_issuer_id: values.asc_issuer_id || undefined,
      asc_key_id: values.asc_key_id || undefined,
      // Only send the key when the admin pasted a new one — empty leaves the stored key untouched.
      ...(values.asc_private_key_pem
        ? { asc_private_key_pem: values.asc_private_key_pem }
        : {}),
    };
    save.mutate(req);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      <Form>
        <Flex direction="column" gap="4">
          <Flex align="center" gap="2">
            {data?.is_configured ? (
              <>
                <CheckCircle2 size={16} color="var(--green-9)" />
                <Text size="2" weight="medium">Configured</Text>
                <Badge color="green">{data.environment ?? "sandbox"}</Badge>
              </>
            ) : (
              <>
                <Clock size={16} color="var(--orange-9)" />
                <Text size="2" weight="medium">Not configured</Text>
                <Badge color="orange">incomplete</Badge>
              </>
            )}
          </Flex>

          {data?.is_configured && (
            <Card>
              <Flex direction="column" gap="2">
                <Text size="2" weight="medium" as="div">
                  Test ASSN delivery
                </Text>
                <Text size="2" color="gray" as="div">
                  Triggers a real TEST event from Apple's App Store Server API to the URL
                  registered in App Store Connect for that environment. Sandbox and production
                  are independent — passing one does <b>not</b> validate the other.
                </Text>
                <Flex gap="3" wrap="wrap" mt="1">
                  <TestEnvironmentPanel
                    env="sandbox"
                    state={testStates.sandbox}
                    onRun={() => runTest("sandbox")}
                  />
                  <TestEnvironmentPanel
                    env="production"
                    state={testStates.production}
                    onRun={() => runTest("production")}
                  />
                </Flex>
              </Flex>
            </Card>
          )}

          <Callout.Root size="1" color="blue">
            <Callout.Icon><Info size={14} /></Callout.Icon>
            <Callout.Text>
              Create (or reuse) the auto-renewable subscription products in App Store
              Connect, then paste each product's identifier into the matching plan's
              "Apple product ID" field. The In-App Purchase key below is separate from the
              products — it lets Anythink verify purchases server-side and keep
              subscription status in sync. Existing products carry over; the key is new.
            </Callout.Text>
          </Callout.Root>

          <InputField
            name="bundle_id"
            label="App bundle ID"
            required
            type="text"
            description="Your iOS app's bundle identifier, e.g. com.yourcompany.app. Must match the app making purchases."
          />

          <SelectField
            name="environment"
            label="Default environment"
            required
            type="string"
            description="Default App Store environment. Sandbox for TestFlight/App Review, Production for live. The true environment is auto-detected per purchase — this is just the fallback hint."
          >
            <SelectField.Item value="sandbox">Sandbox</SelectField.Item>
            <SelectField.Item value="production">Production</SelectField.Item>
          </SelectField>

          <InputField
            name="asc_issuer_id"
            label="App Store Connect Issuer ID"
            type="text"
            description="App Store Connect → Users and Access → Integrations → App Store Connect API. The account-level Issuer ID (a UUID) shown above the keys list — the same value applies to In-App Purchase keys, which don't display their own. Powers App Store Server API calls: status sync, restore, and renewals."
          />

          <InputField
            name="asc_key_id"
            label="App Store Connect Key ID"
            type="text"
            description="Generate a key under Users and Access → Integrations → In-App Purchase (needs the Admin role), then paste its Key ID here. Download the .p8 at creation — Apple only offers it once."
          />

          {data?.has_private_key && (
            <Callout.Root size="1" color="green">
              <Callout.Icon><KeyRound size={14} /></Callout.Icon>
              <Callout.Text>
                A private key is stored (encrypted). Leave the field below blank to keep it,
                or paste a new .p8 to replace it.
              </Callout.Text>
            </Callout.Root>
          )}

          <InputField
            name="asc_private_key_pem"
            label={data?.has_private_key ? "Private key (.p8) — leave blank to keep current" : "Private key (.p8)"}
            as="textarea"
            options={{ rows: 6, placeholder: "-----BEGIN PRIVATE KEY-----\n…\n-----END PRIVATE KEY-----" }}
            description="Paste the contents of the .p8 file downloaded from App Store Connect. Stored encrypted; never shown again after saving."
          />

          <Callout.Root size="1" color="amber">
            <Callout.Icon><Bell size={14} /></Callout.Icon>
            <Callout.Text>
              <Text weight="bold" as="div" mb="1">App Store Server Notifications (required)</Text>
              Cancellations, refunds, and expiries sync back automatically, register this URL in
              App Store Connect → your app → <b>App Information → App Store Server Notifications</b>,
              and set it for both Production and Sandbox:
              <Box my="1">
                <Code size="1">
                  {data?.notification_url ?? "(payments service URL not configured)"}
                </Code>
              </Box>
            </Callout.Text>
          </Callout.Root>

          <Box>
            <SaveButton pending={save.isPending} />
          </Box>
        </Flex>
      </Form>
    </Formik>
  );
}
